/**
 * Fotos vor Ort (PV-B09 bis PV-B11, PV-M03).
 *
 * Drei Dinge sind hier wichtig und stehen deshalb im Code, nicht nur in der
 * Spec:
 *
 *   Die Aufnahme wartet nie auf den Upload. Das Bild landet lokal, der Rest
 *   passiert beim naechsten Abgleich.
 *   Vor dem Speichern wird verkleinert. Ein Tag mit Originalen von einer
 *   heutigen Handykamera fuellt jedes Tablet.
 *   Der lokale Platz wird erst freigegeben, wenn der Server bestaetigt hat.
 */

import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { rufeServer } from '../daten/abgleich.ts';
import { db, neueId, speichere } from '../daten/db.ts';
import type { Foto, Fotokategorie } from '../typen.ts';

const KATEGORIEN: Array<{ wert: Fotokategorie; text: string }> = [
  { wert: 'dach_vorher', text: 'Dach vorher' },
  { wert: 'module', text: 'Module' },
  { wert: 'wechselrichter', text: 'Wechselrichter' },
  { wert: 'zaehlerkasten', text: 'Zählerkasten' },
  { wert: 'ueberspannung_dc', text: 'Überspannungsableiter DC' },
  { wert: 'ueberspannung_ac', text: 'Überspannungsableiter AC' },
  { wert: 'wlv_zk', text: 'WLV im Zählerkasten' },
  { wert: 'wlv_wr', text: 'WLV am Wechselrichter' },
  { wert: 'wlv_einstellung_wr', text: 'WLV-Einstellung im Wechselrichter' },
  { wert: 'abnahme', text: 'Abnahme' },
  { wert: 'sonstiges', text: 'Sonstiges' },
];

/** Kantenlaenge, auf die verkleinert wird. Fuer Dokumentationszwecke reicht das. */
const KANTENLAENGE = 2000;

interface Eigenschaften {
  projektId: string;
  flaecheId: string | null;
}

export function Fotos({ projektId, flaecheId }: Eigenschaften): React.JSX.Element {
  const [kategorie, setKategorie] = useState<Fotokategorie>('dach_vorher');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const fotos = useLiveQuery(
    () =>
      db.fotos
        .where('projekt_id')
        .equals(projektId)
        .filter((f) => !f.geloescht_am)
        .toArray(),
    [projektId],
    [] as Foto[],
  );

  async function aufnehmen(dateien: FileList | null): Promise<void> {
    if (!dateien || dateien.length === 0) return;
    setLaeuft(true);
    setFehler(null);
    const nichtGelesen: string[] = [];
    try {
      for (const datei of Array.from(dateien)) {
        // Ein Bild, das sich nicht lesen lässt (HEIC, kaputt), hielt vorher
        // still alle folgenden der Auswahl auf.
        let verkleinert: Awaited<ReturnType<typeof verkleinere>>;
        try {
          verkleinert = await verkleinere(datei, KANTENLAENGE);
        } catch {
          nichtGelesen.push(datei.name);
          continue;
        }
        const foto: Foto = {
          id: neueId(),
          seq: null,
          schmutzig: true,
          geaendert_am: new Date().toISOString(),
          geloescht_am: null,
          projekt_id: projektId,
          flaeche_id: flaecheId,
          kategorie,
          ablage_schluessel: null,
          dateiname: datei.name,
          groesse_bytes: verkleinert.blob.size,
          breite_px: verkleinert.breite,
          hoehe_px: verkleinert.hoehe,
          aufgenommen_am: new Date(datei.lastModified).toISOString(),
          bemerkung: null,
          hochgeladen: false,
        };
        await db.fotodateien.put({ foto_id: foto.id, blob: verkleinert.blob });
        await speichere('fotos', foto, {
          projekt_id: foto.projekt_id,
          flaeche_id: foto.flaeche_id,
          kategorie: foto.kategorie,
          dateiname: foto.dateiname,
          groesse_bytes: foto.groesse_bytes,
          breite_px: foto.breite_px,
          hoehe_px: foto.hoehe_px,
          aufgenommen_am: foto.aufgenommen_am,
        });
      }
    } finally {
      setLaeuft(false);
      if (nichtGelesen.length > 0) {
        setFehler(`Nicht gelesen: ${nichtGelesen.join(', ')} — das Format kennt dieses Gerät nicht.`);
      }
    }
  }

  async function loesche(foto: Foto): Promise<void> {
    const warnung = foto.hochgeladen
      ? 'Foto entfernen?'
      : 'Dieses Foto ist noch nicht am Server. Entfernt ist es danach endgültig weg. Trotzdem entfernen?';
    if (!confirm(warnung)) return;
    // Erst die Zeile, dann die Datei — umgekehrt bliebe bei einem Abbruch eine
    // Zeile ohne Bild.
    await speichere('fotos', foto, { geloescht_am: new Date().toISOString() });
    await db.fotodateien.delete(foto.id);
  }

  const offen = fotos.filter((f) => !f.hochgeladen).length;

  return (
    <div>
      <div className="feld">
        <label htmlFor="fotokategorie">Kategorie</label>
        <select
          id="fotokategorie"
          value={kategorie}
          onChange={(ereignis) => setKategorie(ereignis.target.value as Fotokategorie)}
        >
          {KATEGORIEN.map((k) => (
            <option key={k.wert} value={k.wert}>
              {k.text}
            </option>
          ))}
        </select>
      </div>

      {/* Zwei Wege: Kamera und Galerie. Mit "capture" allein oeffnete das
          Tablet nur die Kamera — ein schon gemachtes Foto (vom Handy, vom
          Kunden geschickt) kam nicht hinein. */}
      <div className="knopfreihe">
        <label className="dateiknopf wichtig">
          {laeuft ? 'Wird verkleinert …' : 'Foto aufnehmen'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={(ereignis) => {
              void aufnehmen(ereignis.target.files);
              ereignis.target.value = '';
            }}
          />
        </label>
        <label className="dateiknopf">
          Aus der Galerie
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(ereignis) => {
              void aufnehmen(ereignis.target.files);
              ereignis.target.value = '';
            }}
          />
        </label>
      </div>

      {fehler && <div className="hinweis hinweis--fehler">{fehler}</div>}
      <p className="klein">
        {fotos.length} Foto(s) am Projekt
        {offen > 0 ? ` · ${offen} warten auf den Upload` : ' · alle übertragen'}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
        {fotos.map((foto) => (
          <Fotokachel key={foto.id} foto={foto} onLoeschen={() => void loesche(foto)} />
        ))}
      </div>
    </div>
  );
}

function Fotokachel({
  foto,
  onLoeschen,
}: {
  foto: Foto;
  onLoeschen: () => void;
}): React.JSX.Element {
  const quelle = useFotoadresse(foto.id);

  return (
    <figure style={{ margin: 0 }}>
      <img
        src={quelle ?? undefined}
        alt={KATEGORIEN.find((k) => k.wert === foto.kategorie)?.text ?? 'Foto'}
        style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', background: '#f7f7f7' }}
      />
      <figcaption style={{ fontSize: '0.65rem', color: 'var(--grau)' }}>
        {KATEGORIEN.find((k) => k.wert === foto.kategorie)?.text}
        {!foto.hochgeladen && ' · offen'}
        <button onClick={onLoeschen} className="schlicht" title="Foto entfernen" aria-label="Foto entfernen">
          ×
        </button>
      </figcaption>
    </figure>
  );
}

/**
 * Wo das Bild herkommt: vom Gerät, solange es dort liegt, sonst vom Server.
 *
 * Vom Server geht es nur mit Anmeldung — ein `<img src="/api/…">` schickt die
 * nicht mit, und Fotos, die schon hochgeladen waren, blieben leer. Die
 * Adresse wird freigegeben, wenn die Kachel verschwindet; vorher hielt jedes
 * Öffnen des Reiters alle Bilder ein weiteres Mal im Speicher.
 */
export function useFotoadresse(fotoId: string): string | null {
  // `null`: nicht am Gerät. `undefined`: Die Abfrage läuft noch.
  const lokal = useLiveQuery(() => db.fotodateien.get(fotoId).then((d) => d ?? null), [fotoId]);
  const [adresse, setAdresse] = useState<string | null>(null);
  useEffect(() => {
    if (lokal === undefined) return;
    let url: string | null = null;
    let vorbei = false;
    void (async () => {
      const blob = lokal?.blob ?? (await holeVomServer(fotoId));
      if (vorbei || !blob) return;
      url = URL.createObjectURL(blob);
      setAdresse(url);
    })();
    return () => {
      vorbei = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fotoId, lokal]);
  return adresse;
}

async function holeVomServer(fotoId: string): Promise<Blob | null> {
  try {
    const antwort = await rufeServer(`/api/fotos/${fotoId}/datei`);
    return antwort.ok ? await antwort.blob() : null;
  } catch {
    return null;
  }
}

/**
 * Verkleinern auf eine sinnvolle Kantenlaenge. Das Original wird verworfen —
 * fuer die Dokumentation reicht das, und der Geraetespeicher ist sonst nach
 * einem Tag voll.
 */
async function verkleinere(
  datei: File,
  kante: number,
): Promise<{ blob: Blob; breite: number; hoehe: number }> {
  const bild = await createImageBitmap(datei);
  const faktor = Math.min(1, kante / Math.max(bild.width, bild.height));
  const breite = Math.round(bild.width * faktor);
  const hoehe = Math.round(bild.height * faktor);

  const leinwand = document.createElement('canvas');
  leinwand.width = breite;
  leinwand.height = hoehe;
  const stift = leinwand.getContext('2d');
  if (!stift) throw new Error('Kein Zeichenbereich verfügbar');
  stift.drawImage(bild, 0, 0, breite, hoehe);
  bild.close();

  const blob = await new Promise<Blob | null>((fertig) =>
    leinwand.toBlob(fertig, 'image/jpeg', 0.82),
  );
  if (!blob) throw new Error('Bild konnte nicht verkleinert werden');
  return { blob, breite, hoehe };
}
